import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";
import { map } from "rxjs/operators";
import { ContractDetailsApiService } from "src/app/core/api/contract-details-api.service";
import { ItemNumberApiService } from "src/app/core/api/item-number-api.service";
import { ContractDetails } from "src/app/core/models/contract-details";
import { ItemNumber } from "src/app/core/models/item-number";
import { ExtensionOverviewRow } from "src/app/core/models/extension-overview-row";

interface EditableItem {
    item: ItemNumber;
    missingFields: string[];
}

interface EditableDetail {
    detail: ContractDetails;
    missingFields: string[];
    noItemNumbers: boolean;
    items: EditableItem[];
}

@Component({
    selector: "app-fill-missing-data-modal",
    standalone: true,
    imports: [CommonModule, FormsModule, TranslatePipe],
    templateUrl: "./fill-missing-data-modal.component.html",
    styleUrl: "./fill-missing-data-modal.component.scss",
})
export class FillMissingDataModalComponent implements OnInit {
    @Input() public row!: ExtensionOverviewRow;

    public loading = true;
    public saving = false;
    public editableDetails: EditableDetail[] = [];

    constructor(
        private activeModal: NgbActiveModal,
        private contractDetailsApiService: ContractDetailsApiService,
        private itemNumberApiService: ItemNumberApiService,
        private translateService: TranslateService,
        private toastrService: ToastrService
    ) {}

    ngOnInit(): void {
        const missingDetails = this.row.missing?.details ?? [];

        this.contractDetailsApiService
            .getByOwner({ extensionId: this.row.extensionId })
            .subscribe({
                next: (details) => {
                    const itemFetches = missingDetails
                        .filter((md) => md.items && md.items.length > 0)
                        .map((md) =>
                            this.itemNumberApiService
                                .getByContractDetails(md.contractDetailsId)
                                .pipe(map((items) => ({ contractDetailsId: md.contractDetailsId, items })))
                        );

                    const items$ = itemFetches.length > 0 ? forkJoin(itemFetches) : of([]);
                    items$.subscribe({
                        next: (itemResults) => {
                            this.buildModel(details, itemResults);
                            this.loading = false;
                        },
                        error: () => this.loading = false,
                    });
                },
                error: () => this.loading = false,
            });
    }

    private buildModel(
        details: ContractDetails[],
        itemResults: { contractDetailsId: number; items: ItemNumber[] }[]
    ): void {
        const missingDetails = this.row.missing?.details ?? [];
        this.editableDetails = missingDetails
            .map((md) => {
                const detail = details.find((d) => d.id === md.contractDetailsId);
                if (!detail) {
                    return null;
                }
                const fetchedItems = itemResults.find((r) => r.contractDetailsId === md.contractDetailsId)?.items ?? [];
                const items: EditableItem[] = (md.items ?? [])
                    .map((im) => {
                        const item = fetchedItems.find((i) => i.id === im.itemNumberId);
                        return item ? { item, missingFields: im.missingFields } : null;
                    })
                    .filter((x): x is EditableItem => x !== null);

                return {
                    detail,
                    missingFields: md.missingFields ?? [],
                    noItemNumbers: md.noItemNumbers,
                    items,
                } as EditableDetail;
            })
            .filter((x): x is EditableDetail => x !== null);
    }

    public isMissing(fields: string[], field: string): boolean {
        return fields.includes(field);
    }

    /** Nothing is directly editable here (only structural gaps) — hide the Save button. */
    public get hasEditableFields(): boolean {
        return this.editableDetails.some(
            (d) => d.missingFields.length > 0 || d.items.length > 0
        );
    }

    public onSave(): void {
        const saves = [];
        for (const ed of this.editableDetails) {
            if (ed.missingFields.length > 0) {
                saves.push(this.contractDetailsApiService.update(ed.detail));
            }
            for (const ei of ed.items) {
                saves.push(this.itemNumberApiService.patch(ei.item));
            }
        }

        if (saves.length === 0) {
            this.activeModal.dismiss();
            return;
        }

        this.saving = true;
        forkJoin(saves).subscribe({
            next: () => {
                this.translateService
                    .get("extensionsOverview.modal.saved")
                    .subscribe((res: string) => this.toastrService.success(res));
                this.saving = false;
                this.activeModal.close(true);
            },
            error: () => {
                this.translateService
                    .get("extensionsOverview.modal.saveError")
                    .subscribe((res: string) => this.toastrService.error(res));
                this.saving = false;
            },
        });
    }

    public onClose(): void {
        this.activeModal.dismiss();
    }
}
